// src/components/Onboarding.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { awardXP } from '../utils/xpAwards';
import { LinearProgress, Tooltip, Alert } from '@mui/material';
import { createLogger } from '../utils/logger';
import { ModernFileService } from '../utils/modernFileService';
import { FileMetadata, FileUsageType, FileUsageContext } from '../types/files';
import { useAuthState } from '../hooks/useAuthState';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import FocusTypeSelector from './Focus/FocusTypeSelector';
import FocusSetupFlow from './Focus/FocusSetupFlow';

const logger = createLogger({ module: 'Onboarding' });
const fileService = ModernFileService.getInstance();

interface OnboardingProps {
  onComplete: () => void;
  // Optional experimental props for A/B testing
  experimentalStepCount?: number;
  bannerVariant?: string;
  styleVariant?: string;
}

interface CardData {
  title: string;
  overview: string;
  [key: string]: any;
}

interface CareerCardType {
  type: 'Project' | 'Training' | 'CareerGoal';
  card_data: CardData;
  [key: string]: any;
}

const Onboarding: React.FC<OnboardingProps> = ({ 
  onComplete, 
  experimentalStepCount,
  bannerVariant,
  styleVariant 
}) => {
  const [step, setStep] = useState(0);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [firstNote, setFirstNote] = useState('');
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedCards, setGeneratedCards] = useState<any[]>([]);
  const [generatedWorkExperience, setGeneratedWorkExperience] = useState<any[]>([]);
  const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set());
  const [selectedWorkExperience, setSelectedWorkExperience] = useState<Set<number>>(new Set());
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [hasExistingResume, setHasExistingResume] = useState(false);
  const [existingResumePath, setExistingResumePath] = useState('');
  const [hasExistingNote, setHasExistingNote] = useState(false);
  const [existingNote, setExistingNote] = useState<{ id: string; title: string; content: string } | null>(null);
  const [isGeneratingCards, setIsGeneratingCards] = useState(false);
  const [cardGenerationTimeout, setCardGenerationTimeout] = useState(false);
  const [hasGeneratedCards, setHasGeneratedCards] = useState(false);
  const [previouslyGeneratedCards, setPreviouslyGeneratedCards] = useState<any[]>([]);
  const { user, isAuthenticated } = useAuthState();
  const { updateOnboardingStatus } = useAuth();
  const { darkMode } = useDarkMode();
  const [selectedFocusType, setSelectedFocusType] = useState<string | null>(null);
  const [showFocusSetup, setShowFocusSetup] = useState(false);

  useEffect(() => {
    if (progress === 100) {
      const timer = setTimeout(() => {
        setShowProgressBar(false);
      }, 3000); // Hide progress bar 3 seconds after completion
      return () => clearTimeout(timer);
    } else if (progress > 0) {
      setShowProgressBar(true);
    }
  }, [progress]);

  useEffect(() => {
    if (generatedCards.length > 0) {
      setSelectedCards(new Set(generatedCards.map((_, index) => index)));
    }
  }, [generatedCards]);

  useEffect(() => {
    if (generatedWorkExperience.length > 0) {
      setSelectedWorkExperience(new Set(generatedWorkExperience.map((_, index) => index)));
    }
  }, [generatedWorkExperience]);

  useEffect(() => {
    if (user) {
      checkForExistingResume();
      checkForExistingNotes();
      checkForExistingCareerCards().then(hasCards => {
        if (hasCards) {
          setHasGeneratedCards(true);
        }
      });
    }
  }, [user]);

  useEffect(() => {
    if (hasExistingNote && step === 5) {
      // If we're on the "Create Your First Note" step and there's an existing note, skip to the next step
      setStep(6);
    }
  }, [hasExistingNote, step]);

  const steps = [
    { title: 'Getting Started', content: 'Learn about Career Card and how it can help your professional journey.' },
    { title: 'Career Cards', content: 'Understand how Career Cards capture your professional experiences.' },
    { title: 'Notes', content: 'See how Notes help you track your daily professional journey.' },
    { title: 'Upload Resume', content: 'Upload your resume to automatically create project cards.' },
    { title: 'Generated Career Cards and Work Experience', content: 'Here are the career cards and work experiences we\'ve generated from your resume.' },
    { title: 'Create Your First Note', content: 'Start by creating your first note.' },
    { title: 'Career Focus', content: 'Set up a career focus to get personalized tracking and insights.' },
    { title: 'Explore', content: `Great! Your journey to start exploring Career Cards is just seconds away!` },
  ];

  const checkForExistingResume = async () => {
    if (!user) return;

    try {
      const response = await fileService.listFiles({
        filters: {
          userId: user.id,
          usageType: 'resume'
        },
        pageSize: 1
      });

      if (response.files && response.files.length > 0 && response.files[0].file_path) {
        setHasExistingResume(true);
        setExistingResumePath(response.files[0].file_path);
      }
    } catch (error) {
      logger.error('Error checking for existing resume:', error);
    }
  };

  const checkForExistingNotes = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('notes')
        .select('id, title, content')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        setHasExistingNote(true);
        setExistingNote(data[0]);
      }
    } catch (error) {
      console.error('Error checking for existing notes:', error);
    }
  };

  const checkForExistingCareerCards = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('career_cards')
        .select('cardid')
        .eq('user_id', user.id)
        .limit(1);

      if (error) throw error;

      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking for existing career cards:', error);
      return false;
    }
  };

  const handleResumeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && user) {
      setResumeFile(file);
      setIsProcessing(true);
      setProgress(0);
      setProgressMessage('Uploading resume...');

      try {
        // If there's an existing resume, delete it first
        if (hasExistingResume && existingResumePath) {
          const existingFile: FileMetadata = {
            id: '',
            user_id: user.id,
            file_name: '',
            mime_type: '',
            size: 0,
            file_path: existingResumePath,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            usage: {
              type: 'resume',
              context: 'resume_upload'
            },
            source: 'local'
          };
          await fileService.deleteFile(existingFile);
        }

        // Upload new resume
        const uploadedFile = await fileService.uploadFile(file, {
          generateThumbnail: true,
          validateQuota: true,
          usage: {
            type: 'resume',
            context: 'resume_upload'
          }
        });

        const hasExistingCards = await checkForExistingCareerCards();
        if (hasExistingCards) {
          setProgress(100);
          setProgressMessage('Existing Career Cards Found!');
          setHasGeneratedCards(true);
          setStep(5); // Skip to Create First Note step
        } else if (uploadedFile.file_path) {
          await generateCareerCards(uploadedFile.file_path);
          
          // Specifically ensure we go to step 4 after generating cards
          // Only go to step 4 if we actually have cards or experiences
          if (generatedCards.length > 0 || generatedWorkExperience.length > 0) {
            setStep(4);
          } else {
            // If nothing was generated, go to step 5
            setStep(5);
          }
        }

        setProgress(100);
        setProgressMessage('Process completed!');
        setHasExistingResume(true);
        if (uploadedFile.file_path) {
          setExistingResumePath(uploadedFile.file_path);
        }
      } catch (error) {
        logger.error('Error in resume upload process:', error);
        alert('Failed to complete resume upload process. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const generateCareerCards = async (filePath: string) => {
    try {
      setIsGeneratingCards(true);
      setProgress(50);
      setProgressMessage('Generating career cards from your resume...');

      const { data, error } = await supabase.functions.invoke('generate-career-cards-from-resume', {
        body: { filePath }
      });

      if (error) throw error;

      logger.debug('Response from generate-career-cards-from-resume:', data);

      if (data.careerCards && data.workExperience) {
        setGeneratedCards(data.careerCards);
        setGeneratedWorkExperience(data.workExperience);
        logger.debug('Career cards generated:', data.careerCards);
        logger.debug('Work experience generated:', data.workExperience);
        setHasGeneratedCards(true);
        
        // If we have cards or work experience, explicitly set to step 4
        if (data.careerCards.length > 0 || data.workExperience.length > 0) {
          // Use setTimeout to ensure state has been updated
          setTimeout(() => setStep(4), 50);
        }
      }

      setProgress(100);
      setProgressMessage('Career cards generated successfully!');
      return data;
    } catch (error) {
      throw error;
    } finally {
      setIsGeneratingCards(false);
    }
  };

  const toggleAllCards = () => {
    if (selectedCards.size === generatedCards.length) {
      // If all are selected, deselect all
      setSelectedCards(new Set());
    } else {
      // Otherwise, select all
      setSelectedCards(new Set(generatedCards.map((_, index) => index)));
    }
  };

  const toggleCardSelection = (index: number) => {
    setSelectedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const CareerCard: React.FC<{ card: CareerCardType }> = ({ card }) => {
    const getCardColor = (type: string) => {
      switch (type) {
        case 'Project': return 'bg-green-500';
        case 'Training': return 'bg-blue-500';
        case 'CareerGoal': return 'bg-yellow-500';
        default: return 'bg-gray-500';
      }
    };

    return (
      <div className="rounded-lg overflow-hidden shadow-md">
        <div className={`${getCardColor(card.type)} p-3`}>
          <h3 className="text-white font-bold text-base">{card.card_data.title}</h3>
        </div>
        <div className="bg-white p-3">
          <p className="text-black text-sm line-clamp-3">{card.card_data.overview}</p>
        </div>
      </div>
    );
  };

  const WorkExperienceCard: React.FC<{ experience: any }> = ({ experience }) => {
    return (
      <div className="rounded-lg overflow-hidden shadow-md">
        <div className="bg-purple-500 p-3">
          <h3 className="text-white font-bold text-base">{experience.position} at {experience.company}</h3>
        </div>
        <div className="bg-white p-3">
          <p className="text-black text-sm">{experience.start_date} - {experience.end_date || 'Present'}</p>
          <p className="text-black text-sm line-clamp-3">{experience.description}</p>
        </div>
      </div>
    );
  };

  const handleCreateNote = async () => {
    if (user) {
      setIsProcessing(true);
      try {
        if (!hasExistingNote && firstNote.trim()) {
          // Create new note only if there's no existing note and there's content
          const { data, error } = await supabase
            .from('notes')
            .insert([
              {
                user_id: user.id,
                title: 'My First Note',
                content: firstNote,
                custom_tags: ['Active'],
                note_date: new Date().toISOString()
              }
            ])
            .select();

          if (error) throw error;

          // Award XP for creating first note
          await awardXP('note', {
            action: 'create',
            noteId: data[0].id
          });

          // Suggest tags for the note
          try {
            await supabase.functions.invoke('suggest-tags', {
              body: {
                noteId: data[0].id,
                content: firstNote,
                existingTags: ['Active']
              }
            });
          } catch (tagError) {
            console.error('Error suggesting tags:', tagError);
          }
        }

        // Move to the next step regardless of whether a new note was created or not
        setStep(step + 1);
      } catch (error) {
        console.error('Error creating/updating note:', error);
        alert('Failed to save note. Please try again.');
      } finally {
        setIsProcessing(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleKeepSelectedCards = async () => {
    try {
      setIsProcessing(true);
      setProgress(50);
      setProgressMessage('Saving selected career cards...');

      const cardsToKeep = generatedCards.filter((_, index) => selectedCards.has(index));
      const experiencesToKeep = generatedWorkExperience.filter((_, index) => selectedWorkExperience.has(index));

      // Save career cards
      if (cardsToKeep.length > 0) {
        // Based on the schema from docs/supabase/schemas/README.md
        // The career_cards table has: user_id, cardid (auto-generated), card_data (jsonb)
        // It doesn't have 'type' or 'card_type' columns
        const formattedCards = cardsToKeep.map(card => {
          // Log the card structure to help debug
          logger.debug('Card structure before formatting:', { cardData: JSON.stringify(card) });
          
          // Create the correctly formatted card object
          // The 'type' field needs to go inside card_data, not as a direct column
          const cardData = {
            ...(typeof card.card_data === 'object' ? card.card_data : {}),
            // Store the original type inside card_data for reference
            type: card.type
          };
          
          // Format the card according to the actual schema
          const formattedCard = {
            user_id: user?.id,
            // Note: cardid will be auto-generated by Supabase
            card_data: cardData,
            // Add optional fields if they exist in the source data
            card_version: card.card_version || 1,
            is_pinned: card.is_pinned || false,
            career_card_date: card.career_card_date || new Date().toISOString()
          };
          
          return formattedCard;
        });
        
        logger.debug('Formatted cards for insertion:', { formattedCard: JSON.stringify(formattedCards[0]) });
        
        const { data, error: cardsError } = await supabase
          .from('career_cards')
          .insert(formattedCards)
          .select();

        if (cardsError) {
          logger.error('Error saving career cards:', cardsError);
          throw cardsError;
        }
        
        logger.debug('Saved cards response:', data);
      }

      // Save work experience
      if (experiencesToKeep.length > 0) {
        // Log the raw experience data to debug
        logger.debug('Raw work experience data:', { experienceData: JSON.stringify(experiencesToKeep[0]) });
        
        // Format work experiences according to schema
        const formattedExperiences = experiencesToKeep.map(exp => {
          // Extract only the fields we need according to the work_experience table schema
          // Explicitly omit id and previous_position_id fields, as they must be valid UUIDs
          // Let Supabase generate a new UUID for the id
          const {
            // Fields to remove or handle specially
            id, 
            previous_position_id,
            previous_position, // This isn't in the DB schema
            ...restOfExp
          } = exp;
          
          // Create properly formatted work experience object
          return {
            // Include the rest of the experience data
            ...restOfExp,
            // Set user_id correctly
            user_id: user?.id,
            // Only include previous_position_id if it's a valid UUID
            ...(isValidUUID(previous_position_id) ? { previous_position_id } : { previous_position_id: null }),
            // Ensure compensation is an array
            compensation: Array.isArray(exp.compensation) ? exp.compensation : []
          };
        });
        
        // Log the formatted experience data
        logger.debug('Formatted work experience data:', { formattedExperience: JSON.stringify(formattedExperiences[0]) });
        
        const { data, error: experienceError } = await supabase
          .from('work_experience')
          .insert(formattedExperiences)
          .select();

        if (experienceError) {
          logger.error('Error saving work experiences:', experienceError);
          throw experienceError;
        }
        
        logger.debug('Saved experiences response:', data);
      }

      logger.debug(`${cardsToKeep.length} cards and ${experiencesToKeep.length} work experiences saved successfully`);

      setProgress(100);
      setProgressMessage('Career cards saved successfully!');
      setStep(step + 1);
    } catch (error) {
      logger.error('Error in handleKeepSelectedCards:', error);
      setProgressMessage('Error saving cards. Please try again.');
      setProgress(0);
      
      // Display a more user-friendly error message
      alert('There was an error saving your cards. Please try again or skip this step.');
      // Don't throw here - this would crash the app
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper function to check if a string is a valid UUID
  const isValidUUID = (str: any): boolean => {
    if (!str) return false;
    if (typeof str !== 'string') return false;
    
    // UUID v4 regex pattern
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidPattern.test(str);
  };

  const handleComplete = async () => {
    try {
      if (!isAuthenticated || !user) {
        console.error('User not authenticated');
        return;
      }
      
      await updateOnboardingStatus();
      onComplete?.();
      navigate('/home');
    } catch (error: any) {
      console.error('Error completing onboarding:', error);
    }
  };

  const handleSkip = () => {
    if (isProcessing) {
      setStep(step + 1);
    } else {
      if (step < 4 && generatedCards.length > 0) {
        setStep(4);  // Show career cards
      } else {
        setStep(step + 1);
        setProgress(0);
        setProgressMessage('');
      }
    }
  };

  const renderIntroduction = () => (
    <div className="max-w-2xl mx-auto text-center pb-24 md:pb-4">
      <h1 className="text-3xl font-bold mb-6">Welcome to Career Card</h1>

      <p className="text-xl mb-8">
        Your personal career dashboard for capturing, organizing, and leveraging your professional journey.
      </p>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">What is a Career Card?</h2>
        <p className="text-lg mb-4">
          A Career Card is a dynamic snapshot of your professional experiences, skills, and achievements.
          It's like a living resume entry that grows with you throughout your career.
        </p>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">How Career Card Empowers You</h2>
        <ul className="text-left list-disc list-inside space-y-2">
          <li>Capture your achievements over time through Notes, never forgetting a key accomplishment</li>
          <li>Organize your experiences into meaningful, easy-to-reference cards</li>
          <li>Reflect on your growth with personalized career insights and reviews</li>
          <li>Prepare for interviews and performance reviews with confidence</li>
          <li>Track your progress towards career goals and milestones</li>
        </ul>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Getting Started is Easy</h2>
        <p className="text-lg">
          Begin by uploading your resume, and we'll automatically create your first set of Career Cards.
          From there, you can add, edit, and organize your professional story with ease.
        </p>
      </div>

      <button
        onClick={() => setStep(step + 1)}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-lg transition duration-300"
      >
        Start Documenting Your Career Journey
      </button>
    </div>
  );

  const renderCareerCardsIntro = () => (
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="text-2xl font-semibold mb-4">Career Cards: Your Professional Snapshot</h2>
      <p className="text-lg mb-6">
        Career Cards are dynamic representations of your professional experiences, skills, and achievements.
      </p>
      <ul className="text-left list-disc list-inside space-y-2 mb-6">
        <li>Capture details about projects, roles, and career goals</li>
        <li>Automatically organize your experiences by type and date</li>
        <li>Easily update and refine your career history over time</li>
        <li>Use cards to prepare for interviews or performance reviews</li>
      </ul>
      <div className="flex justify-between">
        <button
          onClick={() => setStep(step - 1)}
          className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg text-lg transition duration-300"
        >
          Back
        </button>
        <button
          onClick={() => setStep(step + 1)}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-lg transition duration-300"
        >
          Next: Discover Notes
        </button>
      </div>
    </div>
  );

  const renderNotesIntro = () => (
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="text-2xl font-semibold mb-4">Notes: Your Daily Professional Journal</h2>
      <p className="text-lg mb-6">
        Notes help you capture your day-to-day professional experiences and insights.
      </p>
      <ul className="text-left list-disc list-inside space-y-2 mb-6">
        <li>Quickly jot down accomplishments, learnings, and ideas</li>
        <li>Automatically tag and categorize your Notes</li>
        <li>Use Notes to reflect on your progress and growth</li>
        <li>Easily search and reference past experiences</li>
        <li>Leverage AI to update your Career Cards through your Notes</li>
      </ul>
      <div className="flex justify-between">
        <button
          onClick={() => setStep(step - 1)}
          className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg text-lg transition duration-300"
        >
          Back
        </button>
        <button
          onClick={() => setStep(step + 1)}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-lg transition duration-300"
        >
          Next: Upload Your Resume
        </button>
      </div>
    </div>
  );

  const renderResumeUpload = () => {
    const handleUseExistingResume = async () => {
      setIsProcessing(true);
      setProgress(10);
      setProgressMessage('Retrieving existing career cards...');
      setShowProgressBar(true);  // Ensure the progress bar is visible
    
      try {
        const hasExistingCards = await checkForExistingCareerCards();
        if (hasExistingCards) {
          setProgress(100);
          setProgressMessage('Existing Career Cards Found!');
          setHasGeneratedCards(true);
          setStep(5); // Skip to Create First Note step
        } else if (previouslyGeneratedCards.length > 0) {
          setGeneratedCards(previouslyGeneratedCards);
          setProgress(100);
          setProgressMessage('Previously Generated Career Cards Retrieved!');
          setStep(4);
        } else if (hasGeneratedCards && generatedCards.length > 0) {
          setProgress(100);
          setProgressMessage('Career Cards Retrieved!');
          setStep(4);
        } else {
          setProgressMessage('Generating new Career Cards...');
          setProgress(20);  // Set some initial progress
          await generateCareerCards(existingResumePath);
          // The progress should be updated to 100 in the generateCareerCards function
        }
      } catch (error) {
        console.error('Error retrieving existing career cards:', error);
        alert('Failed to retrieve existing career cards. Please try again.');
        setProgress(0);
        setProgressMessage('Error occurred. Please try again.');
      } finally {
        setIsProcessing(false);
        // Don't hide the progress bar immediately, let it stay visible for a moment
        setTimeout(() => setShowProgressBar(false), 3000);
      }
    };

    return (
      <div>
        <p>{steps[3].content}</p>
        {hasExistingResume && (
          <div className={`mt-4 mb-4 p-4 rounded-md ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
            <p className="mb-2">You have an existing uploaded resume. Would you like to use it or upload a new one?</p>
            <div className="flex justify-between">
              <button
                onClick={handleUseExistingResume}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Use Existing Resume
              </button>
              <button
                onClick={() => setHasExistingResume(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Upload New Resume
              </button>
            </div>
          </div>
        )}
        {!hasExistingResume && (
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleResumeUpload}
            className="mt-4"
          />
        )}
        <div className="mt-4 flex justify-between">
          <button
            onClick={() => setStep(step - 1)}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Back
          </button>
          {resumeFile || isProcessing || hasExistingResume ? (
            <button
              onClick={() => setStep(generatedCards.length > 0 ? 4 : 5)}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Next
            </button>
          ) : (
            <button
              onClick={() => setStep(5)}
              className="bg-gray-300 text-gray-600 px-4 py-2 rounded hover:bg-gray-400"
            >
              Skip
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderGeneratedCards = () => {
    // Only redirect if BOTH are empty - don't skip this step if we have at least one type of item
    if (generatedCards.length === 0 && generatedWorkExperience.length === 0) {
      setStep(5);
      return null;
    }

    // Define the new card types
    const cardTypeOrder = ['Project', 'Training', 'CareerGoal'];
    
    // Group cards by type with proper typing
    const groupedCards: Record<string, Array<any>> = {};
    cardTypeOrder.forEach(type => {
      groupedCards[type] = [];
    });

    // Populate the groups with cards
    generatedCards.forEach((card) => {
      // Make sure the type exists in our groups before adding
      if (cardTypeOrder.includes(card.type) && groupedCards[card.type]) {
        groupedCards[card.type].push(card);
      }
    });

    // Get types that actually have cards
    const typesWithCards = cardTypeOrder.filter(type => groupedCards[type].length > 0);
    
    // Determine number of columns (number of card types with cards + work experience)
    const numberOfColumns = typesWithCards.length + (generatedWorkExperience.length > 0 ? 1 : 0);
    
    // Dynamic grid columns class
    const gridColsClass = numberOfColumns <= 1 ? 'grid-cols-1' : 
                          numberOfColumns === 2 ? 'grid-cols-2' :
                          numberOfColumns === 3 ? 'grid-cols-3' :
                          numberOfColumns === 4 ? 'grid-cols-4' : 'grid-cols-5';

    const toggleAllCards = () => {
      if (selectedCards.size === generatedCards.length && selectedWorkExperience.size === generatedWorkExperience.length) {
        setSelectedCards(new Set());
        setSelectedWorkExperience(new Set());
      } else {
        setSelectedCards(new Set(generatedCards.map((_, index) => index)));
        setSelectedWorkExperience(new Set(generatedWorkExperience.map((_, index) => index)));
      }
    };

    const toggleCardSelection = (index: number) => {
      setSelectedCards(prev => {
        const newSet = new Set(prev);
        if (newSet.has(index)) {
          newSet.delete(index);
        } else {
          newSet.add(index);
        }
        return newSet;
      });
    };

    const toggleWorkExperienceSelection = (index: number) => {
      setSelectedWorkExperience(prev => {
        const newSet = new Set(prev);
        if (newSet.has(index)) {
          newSet.delete(index);
        } else {
          newSet.add(index);
        }
        return newSet;
      });
    };

    return (
      <div className="flex flex-col h-full">
        <p>{steps[4].content}</p>
        <p className="mt-4 mb-2 text-lg font-semibold">Select the cards and work experiences you want to keep:</p>

        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="toggle-all"
            checked={selectedCards.size === generatedCards.length && selectedWorkExperience.size === generatedWorkExperience.length}
            onChange={toggleAllCards}
            className="form-checkbox h-5 w-5 text-blue-600"
          />
          <label htmlFor="toggle-all" className="ml-2 text-sm font-medium">
            {selectedCards.size === generatedCards.length && selectedWorkExperience.size === generatedWorkExperience.length ? 'Deselect All' : 'Select All'}
          </label>
        </div>

        <div className={`grow overflow-auto mt-4 mb-4 grid ${gridColsClass} gap-6`}>
          {/* Render only card types that have cards */}
          {typesWithCards.map((type) => (
            <div key={type} className="flex-1">
              <h3 className="text-xl font-semibold mb-4">{type}</h3>
              <div className="space-y-4">
                {groupedCards[type].map((card, index) => {
                  const cardIndex = generatedCards.findIndex(c => c === card);
                  return (
                    <div key={index} className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id={`card-${cardIndex}`}
                        checked={selectedCards.has(cardIndex)}
                        onChange={() => toggleCardSelection(cardIndex)}
                        className="form-checkbox h-5 w-5 text-blue-600 mt-1"
                      />
                      <label htmlFor={`card-${cardIndex}`} className="grow">
                        <CareerCard card={card} />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          
          {/* Only show Work Experience column if there are work experiences */}
          {generatedWorkExperience.length > 0 && (
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-4">Work Experience</h3>
              <div className="space-y-4">
                {generatedWorkExperience.map((exp, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id={`work-exp-${index}`}
                      checked={selectedWorkExperience.has(index)}
                      onChange={() => toggleWorkExperienceSelection(index)}
                      className="form-checkbox h-5 w-5 text-blue-600 mt-1"
                    />
                    <label htmlFor={`work-exp-${index}`} className="grow">
                      <WorkExperienceCard experience={exp} />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between items-center mt-6 mb-4">
          <button
            onClick={() => {
              setStep(3);
              setHasGeneratedCards(false);
              setPreviouslyGeneratedCards(generatedCards);
              setGeneratedCards([]);
              setGeneratedWorkExperience([]);
            }}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors text-base font-semibold"
          >
            Re-upload Resume
          </button>

          <button
            onClick={handleKeepSelectedCards}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors text-lg font-semibold max-w-md w-full"
          >
            Keep Selected Items
          </button>
        </div>
      </div>
    );
  };

  const renderCreateFirstNote = () => {
    if (hasExistingNote) {
      setStep(6); // Skip to Explore step
      return null;
    }

    const handleBack = () => {
      if (generatedCards.length > 0) {
        setStep(4); // Generated Career Cards step
      } else {
        setStep(3); // Resume Upload step
      }
    };

    const handleUseExistingNote = () => {
      if (existingNote) {
        setFirstNote(existingNote.content);
      }
      setStep(step + 1);
    };

    return (
      <div>
        <p>{steps[5].content}</p>
        {hasExistingNote && existingNote ? (
          <div className={`mt-4 mb-4 p-4 rounded-md ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
            <p className="mb-2">You have an existing note. Would you like to use it or create a new one?</p>
            <div className="flex justify-between">
              <button
                onClick={handleUseExistingNote}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Use Existing Note
              </button>
              <button
                onClick={() => setHasExistingNote(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Create New Note
              </button>
            </div>
          </div>
        ) : (
          <textarea
            value={firstNote}
            onChange={(e) => setFirstNote(e.target.value)}
            placeholder="Enter your first note here..."
            className={`w-full h-32 p-2 mt-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              darkMode ? 'bg-gray-800 text-gray-100 border-gray-700' : 'bg-white text-black border-gray-300'
            }`}
          />
        )}
        <div className="mt-4 flex justify-between">
          <button
            onClick={handleBack}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Back
          </button>
          {firstNote.trim() || (hasExistingNote && existingNote) ? (
            <button
              onClick={handleCreateNote}
              disabled={isProcessing}
              className={`bg-blue-500 text-white px-4 py-2 rounded ${
                isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
              }`}
            >
              {isProcessing ? 'Saving...' : hasExistingNote ? 'Continue' : 'Save Note and Continue'}
            </button>
          ) : (
            <button
              onClick={() => setStep(step + 1)}
              className="bg-gray-300 text-gray-600 px-4 py-2 rounded hover:bg-gray-400"
            >
              Skip
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCareerFocus = () => {
    if (showFocusSetup && selectedFocusType) {
      return (
        <FocusSetupFlow
          focusType={selectedFocusType}
          isOnboarding={true}
          onComplete={() => {
            setShowFocusSetup(false);
            setStep(step + 1);
          }}
          onCancel={() => {
            setShowFocusSetup(false);
            setSelectedFocusType(null);
          }}
          allowRetroactive={true}
        />
      );
    }

    return (
      <FocusTypeSelector
        availableTypes={['internship']} // Only show internship for MVP
        onSelect={(focusType) => {
          if (focusType) {
            setSelectedFocusType(focusType);
            setShowFocusSetup(true);
          } else {
            // Skip focus setup
            setStep(step + 1);
          }
        }}
        onSkip={() => setStep(step + 1)}
        showSkip={true}
        title="What's your current career focus?"
        subtitle="Track your progress with specialized tools and insights"
      />
    );
  };

  const renderExplore = () => (
    <div>
      <p>{steps[7].content}</p>
      {isGeneratingCards ? (
        <>
          <div className="mt-4 flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2">Finishing initial Career Card suggestions...</span>
          </div>
          {cardGenerationTimeout && (
            <p className="mt-2 text-yellow-600">
              Card generation is taking longer than expected. You can continue or wait.
            </p>
          )}
        </>
      ) : null}
      {!isGeneratingCards || cardGenerationTimeout ? (
        <button
          onClick={handleComplete}
          className="bg-green-500 text-white px-4 py-2 rounded mt-4 hover:bg-green-600 transition-colors"
        >
          Start Exploring
        </button>
      ) : null}
    </div>
  );

  const renderProgressBar = () => {
    if (showProgressBar || isProcessing) {
      return (
        <div className={`fixed bottom-0 left-0 w-full p-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <LinearProgress variant="determinate" value={progress} />
          <p className="text-center mt-2">{progressMessage}</p>
        </div>
      );
    }
    return null;
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return renderIntroduction();
      case 1:
        return renderCareerCardsIntro();
      case 2:
        return renderNotesIntro();
      case 3:
        return renderResumeUpload();
      case 4:
        return renderGeneratedCards();
      case 5:
        return renderCreateFirstNote();
      case 6:
        return renderCareerFocus();
      case 7:
        return renderExplore();
      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-black'}`}>
      <div className={`mx-auto pt-10 px-4 flex flex-col h-screen ${step === 4 ? 'max-w-6xl' : 'max-w-2xl'}`}>
        <h1 className="text-3xl font-bold mb-6">{steps[step].title}</h1>
        <div className="grow overflow-auto custom-scrollbar touch-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          {renderStepContent()}
        </div>
        {isProcessing && (
          <Alert severity="info" className="mt-4">
            Processing will continue in the background.
          </Alert>
        )}
      </div>
      {renderProgressBar()}
    </div>
  );
};

export default Onboarding;